import classNames from "classnames";
import React from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { GoogleAnalyticsHelper } from "@travellocal/ui";
import { socialUrlBuilder } from "../helpers/socialMediaHelpers";
import { Tooltip } from "./tooltip";

import "./copySocialButtons.scss";

export interface SocialButton {
  platform: string;
  iconClass: string;
  customButtonColor?: string;
}

interface Props {
  pageLink: string;
  shortMessage: string;
  longMessage?: string;
  imageUrl?: string;
  enableCopying?: boolean;
  socialButtons?: SocialButton[];
}

interface State {
  copied: boolean;
}

const gaEventCategory: string = "Share content";
const gaEventAction: string = "Click";

export class SocialButtons extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      copied: false,
    };
  }

  private onCopy = (text, result) => {
    if (!result) {
      return;
    }

    this.setState({ copied: result }, () => {
      // hide copy message after 3.5 seconds
      setTimeout(() => {
        this.setState({ copied: !result });
      }, 3500);
    });
  }

  /**
   * Returns a list of anchor tags, with nested social sharing icons
   */
  private renderButtons = (): JSX.Element[] => {
    const { socialButtons, pageLink, shortMessage, longMessage, imageUrl } = this.props;

    if (socialButtons.length < 1) {
      return null;
    }

    // Create array of anchor tags from socialButtons list
    const socialLinks: JSX.Element[] = socialButtons.map((link) => {
      // generate shareUrl
      const shareUrl = socialUrlBuilder(link.platform);
      const socialButtonClass = classNames(
        "social-button kathmandu-icon",
        link.iconClass,
      );

      switch (link.platform) {
        case "twitter":
          return (
            <Tooltip text="Twitter" textLength="small" key={link.platform}>
              <a href={shareUrl(pageLink, shortMessage)}
                target="_blank"
                onClick={() => GoogleAnalyticsHelper.logEvent(gaEventCategory, gaEventAction, "Twitter")}
              >
                <i className={socialButtonClass} />
              </a>
            </Tooltip>
          );
        case "facebook":
          return (
            <Tooltip text="Facebook" textLength="small" key={link.platform}>
              <a href={shareUrl(pageLink, shortMessage)}
                target="_blank"
                onClick={() => GoogleAnalyticsHelper.logEvent(gaEventCategory, gaEventAction, "Facebook")}
              >
                <i className={socialButtonClass} />
              </a>
            </Tooltip>
          );
        case "linkedin":
          return (
            <Tooltip text="LinkedIn" textLength="small" key={link.platform}>
              <a href={shareUrl(pageLink, shortMessage, longMessage || shortMessage)}
                target="_blank"
                onClick={() => GoogleAnalyticsHelper.logEvent(gaEventCategory, gaEventAction, "LinkedIn")}
              >
                <i className={socialButtonClass} />
              </a>
            </Tooltip>
          );
        case "pinterest":
          return (
            <Tooltip text="Pinterest" textLength="small" key={link.platform}>
              <a href={shareUrl(pageLink, shortMessage, imageUrl)}
                target="_blank"
                onClick={() => GoogleAnalyticsHelper.logEvent(gaEventCategory, gaEventAction, "Pinterest")}
              >
                <i className={socialButtonClass} />
              </a>
            </Tooltip>
          );
        case "whatsapp":
          return (
            <Tooltip text="WhatsApp" textLength="small" key={link.platform}>
              <a href={shareUrl(pageLink, shortMessage)}
                target="_blank"
                onClick={() => GoogleAnalyticsHelper.logEvent(gaEventCategory, gaEventAction, "WhatsApp")}
              >
                <i className={socialButtonClass} />
              </a>
            </Tooltip>
          );
      }
    });

    return socialLinks;
  }

  public render() {
    return (
      <div className="copy-social-buttons">
        <div className="cta-text text--mid">Share</div>
        <div className="social-buttons">

          {this.renderButtons()}

          <div className="copy-link">
            {this.props.enableCopying && (
              <Tooltip text="Copy link" textLength="small">
                <CopyToClipboard onCopy={this.onCopy} text={this.props.pageLink}>
                  <button className="copy-link-button" onClick={() => GoogleAnalyticsHelper.logEvent(gaEventCategory, gaEventAction, "Copy link")}>
                    <i className="icon icon-link" />
                  </button>
                </CopyToClipboard>
              </Tooltip>
            )}
            {this.props.enableCopying && this.state.copied &&
              <div className="copy-confirmation">
                <span>Copied!</span>
              </div>
            }
          </div>
        </div>
      </div>
    );
  }
}
